import React, { useState } from "react";
import {
  Hall,
  HallFloor,
  UpdateHallFloorPayload,
  UpdateHallPayload,
} from "../../interfaces";
import { Button, Form, Popconfirm, Space, Table, TableProps, Tag } from "antd";
import UpdateHallFloorForm from "./UpdateHallFloorForm";
import { useForm } from "antd/lib/form/Form";
import { appSelector } from "../../helpers/appSelector";
import { deleteHall, updateHall } from "../../store/halls";
import { useDispatch } from "react-redux";
import { DeleteOutlined } from "@ant-design/icons";
import { deleteHallFloor, updateHallFloor } from "../../store/hallFloors";

interface Props {
  halls: Hall[];
  loading: boolean;
  selectedHallId?: string;
}

const HallsList = ({ halls, loading, selectedHallId: selectedHall }: Props) => {
  const [floorId, setFloorId] = useState<string>("");
  const [open, setOpen] = useState<boolean>(false);
  const [form] = Form.useForm();

  const dispatch = useDispatch();
  const { hall, success } = appSelector((state) => state.hallsStore);
  const { isHallDeleting, deleteSuccess } = appSelector(
    (state) => state.hallsStore
  );

  const [deletableId, setDeletableId] = useState<string | null>(null);

  const handleOnFinish = (values: UpdateHallFloorPayload) => {
    values.hall_id = selectedHall || "";
    values.id = floorId;
    dispatch(updateHallFloor(values));
    if (success) {
      setOpen(false);
    }
  };

  const handleDelete = (id: string) => {
    const payload = {
      id: id,
      hallId: selectedHall || "",
    };
    dispatch(deleteHallFloor(payload));
    isHallDeleting && setDeletableId(id);
    deleteSuccess && setDeletableId(null);
  };

  const columns: TableProps<HallFloor>["columns"] = [
    {
      title: "Floor Name",
      dataIndex: "name",
      align: "left",
      key: "floor",
      sorter: (a: HallFloor, b: HallFloor) => a.name.localeCompare(b.name),
    },
    {
      title: "Related Hall",
      align: "left",
      key: "hall",
      render: (record: HallFloor) => record.hall?.name,
    },
    {
      title: "Rooms",
      align: "left",
      key: "rooms",
      render: (record: HallFloor) => (
        <Tag color="success" key="room-count">
          {record.rooms?.length}
        </Tag>
      ),
    },
    {
      title: "Description",
      dataIndex: "description",
      align: "left",
      key: "description",
      sorter: (a: HallFloor, b: HallFloor) =>
        (a.description?.length || 0) - (b.description?.length || 0),
    },
    // {
    //   title: "Total Beds",
    //   dataIndex: "total_beds",
    //   align: "center",
    //   key: "total",
    //   sorter: (a: any, b: any) => a.id - b.id,
    // },
    // {
    //   title: "Available Beds",
    //   dataIndex: "available_beds",
    //   align: "center",
    //   key: "available",
    //   sorter: (a: any, b: any) => a.id - b.id,
    // },
    // {
    //   title: "Occupied Beds",
    //   dataIndex: "occupied_beds",
    //   align: "center",
    //   key: "occupied",
    //   sorter: (a: any, b: any) => a.id - b.id,
    // },
    {
      title: "Actions",
      key: "actions",
      align: "center",
      render: (record: HallFloor) => (
        <Space size="middle">
          <Button
            key={`edit-${record.id}`}
            type="primary"
            onClick={() => {
              setFloorId(record.id);
              setOpen(true);
            }}
          >
            Edit
          </Button>
          <Popconfirm
            placement="topLeft"
            title="Are you sure you want to delete this hall?"
            onConfirm={() => handleDelete(record.id)}
            okText="Yes"
            cancelText="No"
          >
            <Button
              key={`delete-${record.id}`}
              danger
              icon={<DeleteOutlined />}
              loading={deletableId == record.id}
            >
              {deletableId == record.id ? "Deleting..." : "Delete"}
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <>
      <Table<HallFloor>
        size="small"
        columns={columns}
        dataSource={halls}
        loading={loading}
        scroll={{ x: true }}
      />

      <UpdateHallFloorForm
        open={open}
        onCancel={() => {
          setOpen(false);
        }}
        form={form}
        onFinish={handleOnFinish}
        id={floorId}
        hallId={selectedHall || ""}
      />
    </>
  );
};

export default HallsList;
